import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { parseISO } from 'date-fns'

import axios from '../../dataClient/dataClient.js'
import { billSchema } from '../../schemas/billSchema.js'
import { calculateBillTotal } from '../../util/strings.js'
import { FlexEndContainer, PaddedForm, StyledDialog, StyledDialogContent } from '../styles/Containers.js'
import { ErrorMessage } from '../common/UserMessage.js'
import Button, { SecondaryButton } from '../common/Button.js'
import BillForm from './BillForm.js'

function NewBillButton({ foiId, onSuccess = () => {}, onlyApplicationFeeAllowed = false }) {
  const [isVisible, setIsVisible] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  const handleOpen = () => setIsVisible(true)
  const handleClose = () => {
    setIsVisible(false)
    reset()
    setError(false)
    setIsLoading(false)
  }

  const methods = useForm({ resolver: yupResolver(billSchema) })
  const { handleSubmit, reset } = methods

  const onError = (errors, e) => console.error('Error', errors, e)

  async function onSubmit(data) {
    setIsLoading(true)
    setError(false)

    const total = calculateBillTotal({ lineItems: data.lineItems })
    const amountDue = (total * data.percentDue) / 100
    const { type, percentDue, lineItems } = data
    const body = { foiId, type, percentDue, lineItems, total, amountDue }

    axios
      .post('/bill', body, { headers: { 'Content-Type': 'application/json' } })
      .then(({ data }) => {
        handleClose()
        onSuccess({
          ...data.data,
          feeLastModified: new Date(data.data.feeLastModified),
          paymentDueDate: data.data.paymentDueDate ? parseISO(data.data.paymentDueDate) : undefined
        })
      })
      .catch(e => {
        console.error('Failed to create the bill', e)
        setError(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const isMounted = useRef(true)

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  return (
    <>
      <Button onClick={handleOpen}>Create New</Button>
      <StyledDialog maxwidth="1400px" open={isVisible} onClose={handleClose} keepMounted={false}>
        <StyledDialogContent>
          <FormProvider {...methods}>
            <PaddedForm onSubmit={handleSubmit(onSubmit, onError)}>
              <BillForm onlyApplicationFeeAllowed={onlyApplicationFeeAllowed} disabled={isLoading} />
              {error && <ErrorMessage>There was an error saving the draft. Please try again.</ErrorMessage>}
              <FlexEndContainer>
                <SecondaryButton onClick={handleClose} disabled={isLoading}>
                  Cancel
                </SecondaryButton>
                <Button type="submit" loading={isLoading}>
                  Save Draft
                </Button>
              </FlexEndContainer>
            </PaddedForm>
          </FormProvider>
        </StyledDialogContent>
      </StyledDialog>
    </>
  )
}

NewBillButton.propTypes = {
  foiId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  onlyApplicationFeeAllowed: PropTypes.bool
}

export default NewBillButton
